import React, { useMemo, useCallback } from 'react';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import { SidebarHeader } from './header/SidebarHeader';
import { TreeView } from './tree/TreeView';
import { WaterfallView } from './waterfall/WaterfallView';
import { TraceActions } from './footer/TraceActions';
import type { TraceItem } from '@/types/trace';
import { filterTraceItems, getAllNodeIds } from '@/utils/traceTransformer';

interface TraceSidebarProps {
  items: TraceItem[];
  trajectoryId?: string;
  className?: string;
}

export function TraceSidebar({ items, trajectoryId, className }: TraceSidebarProps) {
  const { state, expandAll, collapseAll } = useTraceSidebar();

  // Filter items based on filter mode
  const filteredItems = useMemo(() => {
    return filterTraceItems(items, state.filterMode);
  }, [items, state.filterMode]);

  // Get all expandable node IDs for "Expand All"
  const allNodeIds = useMemo(() => getAllNodeIds(items), [items]);

  const handleExpandAll = useCallback(() => {
    expandAll(allNodeIds);
  }, [expandAll, allNodeIds]);

  const handleCollapseAll = useCallback(() => {
    collapseAll();
  }, [collapseAll]);

  return (
    <div className={`flex flex-col h-full bg-card border-r ${className || ''}`}>
      {/* Header with view mode toggle and settings */}
      <SidebarHeader
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
      />

      {/* Main content - Tree or Waterfall view */}
      <div className="flex-1 overflow-hidden">
        {state.viewMode === 'tree' ? (
          <TreeView items={filteredItems} />
        ) : (
          <WaterfallView items={filteredItems} />
        )}
      </div>

      {/* Footer with actions */}
      <TraceActions trajectoryId={trajectoryId} />
    </div>
  );
}

export default TraceSidebar;
